<template>
  <BaseDialog v-model="dialog" maxWidth="1000" title="Functional Location">
    <div class="functional-location-info">
      <table>
        <tr v-for="(_, i) in fields.filter((_, i) => i % 2 === 0)" :key="i">
          <td class="info-title">{{ fields[i * 2].title }}</td>
          <td class="info-value">
            {{ functionallocation[fields[i * 2].key] }}
          </td>
          <td class="info-title" v-if="i * 2 + 1 < fields.length">
            {{ fields[i * 2 + 1].title }}
          </td>
          <td class="info-title" v-else />
          <td class="info-value" v-if="i * 2 + 1 < fields.length">
            {{ functionallocation[fields[i * 2 + 1].key] }}
          </td>
          <td class="info-value" v-else />
        </tr>
      </table>
    </div>

    <v-card-text>
      <v-card-title style="display: flex"
        ><span v-if="functionallocation.is_asset">Asset Details</span>
        <v-spacer />
        <v-checkbox
          v-if="functionallocation.parent_id === null"
          v-model="functionallocation.hide_from_tree_top"
          label="Hide From Top Level"
          :false-value="false"
          :true-value="true"
          variant="outlined"
          density="compact"
          class="mr-4"
          hide-details
          clearable
          :readonly="readonly"
        />
        <v-checkbox
          v-model="functionallocation.is_asset"
          label="Is Asset"
          :false-value="false"
          :true-value="true"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          :readonly="readonly"
        />
      </v-card-title>
      <div class="asset-details" v-if="functionallocation.is_asset">
        <NumberField
          v-model="functionallocation.hours_per_day"
          label="Hours Per Day"
          suffix="hrs"
          variant="outlined"
          density="compact"
          :readonly="readonly"
        />
        <v-autocomplete
          v-model="functionallocation.asset_class_id"
          label="Asset Class"
          :items="assetClasses"
          item-value="id"
          item-title="name"
          clearable
          variant="outlined"
          density="compact"
          hide-details
          :readonly="readonly"
        />
      </div>
    </v-card-text>

    <template v-if="!readonly" #actions>
      <v-btn :loading="loading" @click="save">Save</v-btn>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
import { ref } from "vue";
import BaseDialog from "../base/dialogs/BaseDialog.vue";
import { AssetClass, FunctionalLocation } from "@/classes";
import NumberField from "../base/inputs/NumberField.vue";
import { useListStore } from "@/store/lists";

const props = defineProps<{
  readonly?: boolean;
}>();
const emits = defineEmits(["change"]);

const dialog = ref(false);
const functionallocation = ref<FunctionalLocation>(new FunctionalLocation());
const loading = ref(false);

const lists = useListStore();
const assetClasses = lists.useList<AssetClass>("assetClasses");

const open = async (id: string | null) => {
  dialog.value = true;
  if (id) {
    functionallocation.value = await FunctionalLocation.get(id);
  } else {
    functionallocation.value = new FunctionalLocation();
  }
};

const fields: { title: string; key: keyof FunctionalLocation }[] = [
  { title: "Functional Location", key: "functional_location" },
  {
    title: "Superior Functional Location",
    key: "superior_functional_location",
  },
  {
    title: "Functional Location Description",
    key: "functional_location_description",
  },
  { title: "Construction Type", key: "construction_type" },
  { title: "Planning Plant", key: "planning_plant" },
  { title: "Location", key: "location" },
  { title: "Accounting Assesment", key: "accounting_assesment" },
  { title: "Maintenance Plant", key: "maintenance_plant" },
  { title: "Company Code", key: "company_code" },
  { title: "Cost Center", key: "cost_center" },
  { title: "Planner Group", key: "planner_group" },
  { title: "Sort Field", key: "sort_field" },
  { title: "Main Work Center", key: "main_work_center" },
  { title: "Manufacturer", key: "manufacturer" },
  { title: "Manufacturer Serial Number", key: "manufacturer_serial_number" },
  { title: "Entity ID", key: "entity_id" },
  {
    title: "Functional Location Category",
    key: "functional_location_category",
  },
  { title: "Catalog Profile", key: "catalog_profile" },
  { title: "Object Number", key: "object_number" },
  { title: "Length", key: "length" },
];

const save = async () => {
  loading.value = true;
  await functionallocation.value.save();
  loading.value = false;
  dialog.value = false;
  emits("change", functionallocation.value.id);
};

defineExpose({ open });
</script>

<style lang="scss" scoped>
.functional-location-info {
  display: flex;
  table {
    width: 100%;
    border-collapse: collapse;
  }
  td,
  th {
    border: 1px solid #dddddd;
    padding: 8px;
  }
  tr:nth-child(even) {
    background-color: #eeeeee;
  }

  .info-title {
    text-align: right;
    font-weight: 600;
  }
  .info-value {
    text-align: left;
  }
}
.asset-details {
  display: flex;
  flex-direction: column;
}
</style>
